import React, { useMemo, useState } from "react";
import {
    Box,
    Collapse,
    Grid,
    ListItemIcon,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { format } from "date-fns";
import { MoodType } from "../../models/modelTypes";
import { AverageWellbeingBar } from "../classroom/components/AverageWellbeingBar";
import { NegativeDrivers } from "../classroom/components/NegativeDrivers";
import { StudentWellbeingChart } from "../classroom/students/StudentWellbeingChart";
import { SimpleBlankSlate } from "../../core/components/SimpleBlankSlate";
import { VList } from "../../core/components/VList";
import { VListItem } from "../../core/components/VListItem";
import { VModule } from "../../core/components/VModule";
import { verbalTheme } from "../layout/themes/verbal.theme";
import barchartImg from "../../img/bars.svg";
import barchartImgDark from "../../img/barchart.svg";
import { formatParticipantReference } from "common/build/utils/participantReference";

type ParticipantMoodSectionProps = {
    moods: MoodType[];
    participantId: number;
    participantName?: string;
};

const POSITIVE_MOODS = ["Happy"];
const NEGATIVE_MOODS = ["Sad", "Angry", "Worried"];
const NEUTRAL_MOODS = ["Calm"];

export const ParticipantMoodSection: React.FC<ParticipantMoodSectionProps> = ({
    moods,
    participantId,
    participantName,
}) => {
    const [positiveOpen, setPositiveOpen] = useState(false);
    const [negativeOpen, setNegativeOpen] = useState(false);
    const [neutralOpen, setNeutralOpen] = useState(false);

    const displayName = participantName ?? formatParticipantReference(participantId);
    const positiveMoods = useMemo(() => moods.filter((m) => POSITIVE_MOODS.includes(m.mood)), [moods]);
    const negativeMoods = useMemo(() => moods.filter((m) => NEGATIVE_MOODS.includes(m.mood)), [moods]);
    const neutralMoods = useMemo(() => moods.filter((m) => NEUTRAL_MOODS.includes(m.mood)), [moods]);
    const triggerMoods = useMemo(
        () => moods.filter((m) => m.trigger).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
        [moods],
    );

    return (
        <Box mt={4}>
            <Typography variant="h5" color="textSecondary" gutterBottom>
                Mood history
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {moods.length > 0 ? (
                        <AverageWellbeingBar
                            allMoods={moods}
                            positiveMoods={positiveMoods}
                            negativeMoods={negativeMoods}
                            neutralMoods={neutralMoods}
                        />
                    ) : null}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper
                        sx={{
                            backgroundColor: verbalTheme.palette.secondaryShade.main,
                            borderRadius: 1,
                            mt: 2,
                            pt: 2,
                            height: "100%",
                        }}
                        variant="outlined"
                    >
                        {moods.length > 0 ? (
                            <>
                                <Typography variant="h4" align="center">
                                    All Moods
                                </Typography>
                                <MoodCategoryList
                                    label="Positive"
                                    emoji="😊"
                                    moods={positiveMoods}
                                    open={positiveOpen}
                                    onToggle={() => setPositiveOpen(!positiveOpen)}
                                />
                                <MoodCategoryList
                                    label="Negative"
                                    emoji="😢"
                                    moods={negativeMoods}
                                    open={negativeOpen}
                                    onToggle={() => setNegativeOpen(!negativeOpen)}
                                />
                                <MoodCategoryList
                                    label="Neutral"
                                    emoji="😌"
                                    moods={neutralMoods}
                                    open={neutralOpen}
                                    onToggle={() => setNeutralOpen(!neutralOpen)}
                                />
                            </>
                        ) : (
                            <SimpleBlankSlate
                                image={<img src={barchartImgDark} alt="" />}
                                header="Student Moods"
                                content={`When ${displayName} has marked their mood, moods will be grouped into Positive, Negative and Neutral.`}
                            />
                        )}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <NegativeDrivers allNegativeMoods={negativeMoods} individual name={displayName} />
                </Grid>
                <Grid item xs={12}>
                    <VModule variant="outlined">
                        {triggerMoods.length > 0 ? (
                            <>
                                <Box display="flex" justifyContent="center" flexDirection="column" mb={2}>
                                    <Typography variant="h4" align="center">
                                        {displayName}&apos;s Triggers
                                    </Typography>
                                    <Typography variant="caption" align="center" color="primary">
                                        Concerning moods to facilitate early intervention
                                    </Typography>
                                </Box>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">Mood</TableCell>
                                            <TableCell align="center">Reason</TableCell>
                                            <TableCell align="center">Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {triggerMoods.map((m) => (
                                            <TableRow key={m.id}>
                                                <TableCell align="center">{m.mood}</TableCell>
                                                <TableCell align="center">{m.reason}</TableCell>
                                                <TableCell align="center">
                                                    {format(new Date(m.date), "EE d MMM yyyy")}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        ) : (
                            <SimpleBlankSlate
                                image={<img src={barchartImg} alt="" />}
                                header="Student Triggers"
                                content={`When ${displayName} has mood check-ins that cause a trigger, they will appear here for early intervention.`}
                            />
                        )}
                    </VModule>
                </Grid>
                <Grid item xs={12}>
                    {moods.length > 0 ? (
                        <StudentWellbeingChart moods={moods} />
                    ) : (
                        <Paper variant="outlined">
                            <SimpleBlankSlate
                                image={<img src={barchartImg} alt="" />}
                                header="Wellbeing trend"
                                content={`When ${displayName} begins mood check-ins, their wellbeing trend will appear here.`}
                            />
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

type MoodCategoryListProps = {
    label: string;
    emoji: string;
    moods: MoodType[];
    open: boolean;
    onToggle: () => void;
};

const MoodCategoryList: React.FC<MoodCategoryListProps> = ({ label, emoji, moods, open, onToggle }) => {
    const moodCounts = moods.reduce<Map<string, number>>((counts, entry) => {
        counts.set(entry.mood, (counts.get(entry.mood) ?? 0) + 1);
        return counts;
    }, new Map());

    return (
        <VList style={{ display: "table" }}>
            <VListItem onClick={onToggle}>
                <ListItemIcon>
                    <span style={{ fontSize: "32px" }}>{emoji}</span>
                </ListItemIcon>
                <ListItemText>
                    {label} ({moods.length} total)
                </ListItemText>
                {moods.length > 0 ? open ? <ExpandLess /> : <ExpandMore /> : null}
            </VListItem>
            <Collapse in={open}>
                {[...moodCounts.entries()].map(([mood, count]) => (
                    <VListItem key={mood} button={undefined}>
                        <ListItemText inset>
                            {mood} ({count})
                        </ListItemText>
                    </VListItem>
                ))}
            </Collapse>
        </VList>
    );
};
